import { useEffect, useState } from "react";
import { useListContext, useDataProvider, useNotify } from "ra-core";

import type { Deal } from "@/components/atomic-crm/types";
import {
  groupDealsByStage,
  moveDealInColumns,
  stagesOf,
  usePipelines,
  usePipelineStages,
} from "./dealData";
import { persistDealMove, type DealMove } from "./dealMoves";
import { LostReasonDialog } from "./LostReasonDialog";
import { NextActionDialog } from "./NextActionDialog";
import { needsNextAction } from "./nextAction";
import { usePipelineDeals } from "./usePipelineDeals";

// The permanent filter of the deals list (DealList, TenaxDealListMobile)
export const ACTIVE_DEALS_FILTER = { "archived_at@is": null };

// State shared by the desktop board and the mobile list (task 1.7): the
// pipeline shown (a filter of the deals list, so it is remembered), its
// stages from pipeline_stages, the deals by stage and the moves, with the
// lost reason asked when a deal goes to a lost stage and the next action
// when a deal without one goes to an open stage (task 1.8). The list gives
// the filters only: the deals come from usePipelineDeals, all of them, not
// the first page of the list.
export const useDealBoard = () => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const { filterValues, displayedFilters, setFilters } = useListContext<Deal>();
  const { data: pipelines = [] } = usePipelines();
  const { data: allStages = [] } = usePipelineStages();
  const [optimistic, setOptimistic] = useState<Record<number, Deal[]> | null>(
    null,
  );
  const [pendingLost, setPendingLost] = useState<DealMove | null>(null);
  const [pendingNext, setPendingNext] = useState<DealMove | null>(null);

  const pipelineId: number | undefined =
    filterValues.pipeline_id ?? pipelines[0]?.id;
  const stages = stagesOf(allStages, pipelineId);
  const { data, isPending, refetch } = usePipelineDeals({
    ...filterValues,
    ...ACTIVE_DEALS_FILTER,
    pipeline_id: pipelineId,
  });
  const columns = optimistic ?? groupDealsByStage(data ?? [], stages);

  useEffect(() => {
    if (filterValues.pipeline_id == null && pipelines[0]) {
      setFilters(
        { ...filterValues, pipeline_id: pipelines[0].id },
        displayedFilters,
      );
    }
  }, [filterValues, displayedFilters, pipelines, setFilters]);

  const setPipeline = (id: number) =>
    setFilters({ ...filterValues, pipeline_id: id }, displayedFilters);

  const run = async (move: DealMove) => {
    setOptimistic(
      moveDealInColumns(columns, move.dealId, move.toStageId, move.beforeId),
    );
    try {
      await persistDealMove(dataProvider, stages, move);
    } catch (error) {
      notify(error instanceof Error ? error.message : String(error), {
        type: "error",
      });
    }
    await refetch();
    setOptimistic(null);
  };

  const move = (move: DealMove) => {
    const from = stages.find((stage) => stage.id === move.fromStageId);
    const to = stages.find((stage) => stage.id === move.toStageId);
    if (!to) return;
    if (to.is_lost && !from?.is_lost) {
      setPendingLost(move);
      return;
    }
    const deal = findDeal(move.dealId);
    if (deal && needsNextAction(deal, to)) {
      setPendingNext(move);
      return;
    }
    return run(move);
  };

  const findDeal = (id: DealMove["dealId"]) =>
    Object.values(columns)
      .flat()
      .find((deal) => deal.id === id);
  const pendingDeal = pendingLost ? findDeal(pendingLost.dealId) : undefined;

  const moveDialog =
    pendingLost && pipelineId != null ? (
      <LostReasonDialog
        pipelineId={pipelineId}
        dealName={pendingDeal?.name}
        onCancel={() => setPendingLost(null)}
        onConfirm={(lost) => {
          setPendingLost(null);
          run({ ...pendingLost, lost });
        }}
      />
    ) : pendingNext ? (
      <NextActionDialog
        dealName={findDeal(pendingNext.dealId)?.name}
        onCancel={() => setPendingNext(null)}
        onConfirm={(day) => {
          setPendingNext(null);
          run({ ...pendingNext, nextActionAt: day });
        }}
      />
    ) : null;

  return {
    isPending: isPending || !pipelineId,
    pipelines,
    pipelineId,
    setPipeline,
    stages,
    columns,
    move,
    moveDialog,
  };
};
